import { useEffect } from "react";
import { useLocation } from "react-router-dom";

/** Solo en ficha de película (/watch/…). */
const WATCH_REVEAL_SELECTOR = [
  ".watch-banner",
  ".watch-stage",
  ".watch-cast",
  ".cast-card-btn",
  "[data-reveal]",
].join(",");

/**
 * Entrada suave al cambiar de ruta en todo el portal.
 * Reveal al scroll: solo en la ficha de película.
 */
export function usePageMotion(rootRef) {
  const location = useLocation();
  const isWatch = location.pathname.startsWith("/watch/");

  useEffect(() => {
    const root = rootRef?.current;
    if (!root) return undefined;

    // Entrada de página (todas las rutas).
    root.classList.remove("page-enter-ready");
    void root.offsetWidth;
    root.classList.add("page-enter");
    const ready = window.requestAnimationFrame(() => {
      root.classList.add("page-enter-ready");
    });

    // Quitar restos de reveal de otras páginas.
    root.querySelectorAll(".cs-reveal").forEach((el) => {
      el.classList.remove("cs-reveal", "is-revealed");
      el.style.removeProperty("--reveal-i");
    });

    if (!isWatch) {
      return () => window.cancelAnimationFrame(ready);
    }

    const observed = new WeakSet();
    let io;
    let mo;
    let stagger = 0;

    const prepare = (el) => {
      if (!(el instanceof Element)) return;
      if (!el.classList.contains("cs-reveal")) {
        el.classList.add("cs-reveal");
        el.style.setProperty("--reveal-i", String(stagger % 12));
        stagger += 1;
      }
      el.classList.remove("is-revealed");
    };

    const revealNow = (el) => {
      el.classList.add("is-revealed");
    };

    const inViewport = (el) => {
      const rect = el.getBoundingClientRect();
      const vh = window.innerHeight || 800;
      return rect.top < vh * 0.95 && rect.bottom > 40;
    };

    if (typeof IntersectionObserver === "undefined") {
      root.querySelectorAll(WATCH_REVEAL_SELECTOR).forEach((el) => {
        prepare(el);
        revealNow(el);
      });
      return () => window.cancelAnimationFrame(ready);
    }

    io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          revealNow(entry.target);
          io.unobserve(entry.target);
        });
      },
      { threshold: 0.06, rootMargin: "40px 0px -4% 0px" }
    );

    const watchEl = (el) => {
      if (!(el instanceof Element) || observed.has(el)) return;
      observed.add(el);
      prepare(el);
      if (inViewport(el)) {
        window.requestAnimationFrame(() => revealNow(el));
      } else {
        io.observe(el);
      }
    };

    const scan = () => {
      root.querySelectorAll(WATCH_REVEAL_SELECTOR).forEach(watchEl);
    };

    scan();
    const t1 = window.setTimeout(scan, 100);
    const t2 = window.setTimeout(scan, 450);

    mo = new MutationObserver(() => {
      window.requestAnimationFrame(scan);
    });
    mo.observe(root, { childList: true, subtree: true });

    return () => {
      window.cancelAnimationFrame(ready);
      window.clearTimeout(t1);
      window.clearTimeout(t2);
      io.disconnect();
      mo.disconnect();
    };
  }, [location.pathname, location.search, rootRef, isWatch]);
}
